import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewDays, setRenewDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/accounts', { params: { search, status: statusFilter } });
      setAccounts(data);
    } catch (err) {
      if (err.response?.status === 403) handleLogout();
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  function handleLogout() {
    localStorage.clear();
    navigate('/admin-login');
  }

  async function handleSave(form) {
    try {
      if (form.id) {
        await api.put(`/admin/accounts/${form.id}`, form);
      } else {
        await api.post('/admin/accounts', form);
      }
      setEditing(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Có lỗi xảy ra');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Xoá tài khoản này?')) return;
    await api.delete(`/admin/accounts/${id}`);
    load();
  }

  async function handleToggleLock(acc) {
    await api.put(`/admin/accounts/${acc.id}/lock`, { locked: acc.status !== 'locked' });
    load();
  }

  async function handleRenew() {
    await api.post(`/admin/accounts/${renewTarget.id}/renew`, { days: Number(renewDays) });
    setRenewTarget(null);
    load();
  }

  const statusColor = { active: '#22c55e', expiring_soon: '#f59e0b', expired: '#ef4444', locked: '#64748b' };
  const statusLabel = { active: 'Đang hoạt động', expiring_soon: 'Sắp hết hạn', expired: 'Đã hết hạn', locked: 'Đã khoá' };

  return (
    <div style={s.page}>
      {/* Header Admin */}
      <div style={s.header}>
        <div>
          <h2 style={s.h2}>Quản Trị Hệ Thống</h2>
          <div style={s.subH2}>Tổng số: {accounts.length} tài khoản</div>
        </div>
        <button style={s.logoutBtn} onClick={handleLogout}>Đăng xuất</button>
      </div>

      {/* Thanh công cụ Tìm kiếm / Lọc / Tạo mới (Co giãn Mobile) */}
      <div style={s.toolbar}>
        <input
          style={s.searchBar}
          placeholder="🔍 Tìm username..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select style={s.selectFilter} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">Tất cả trạng thái</option>
          <option value="online">🟢 Đang Online</option>
          <option value="expiring_soon">⏳ Sắp hết hạn</option>
          <option value="expired">❌ Đã hết hạn</option>
          <option value="locked">🔒 Đã khoá</option>
        </select>
        <button style={s.addBtn} onClick={() => setEditing('new')}>+ Tạo Tài Khoản</button>
      </div>

      {/* Danh sách hiển thị dạng Thẻ (Card Grid) — Cực kỳ tối ưu cho Điện thoại */}
      <div style={s.cardsGrid}>
        {accounts.map((acc) => (
          <div key={acc.id} style={s.cardItem}>
            {/* Hàng 1: Username & Trạng thái Online */}
            <div style={s.cardHeader}>
              <div style={s.usernameText}>👤 {acc.username}</div>
              <span style={{ ...s.statusPill, background: statusColor[acc.status] }}>
                {statusLabel[acc.status]}
              </span>
            </div>

            {/* Hàng 2: Họ tên & Ghi chú */}
            <div style={s.cardBody}>
              <div style={s.infoRow}>
                <span style={s.label}>Họ tên:</span>
                <span style={s.value}>{acc.full_name || 'Chưa cập nhật'}</span>
              </div>
              <div style={s.infoRow}>
                <span style={s.label}>Thời hạn:</span>
                <span style={{ ...s.value, fontWeight: '700', color: acc.status === 'expired' ? '#ef4444' : '#38bdf8' }}>
                  {acc.countdown}
                </span>
              </div>
              <div style={s.infoRow}>
                <span style={s.label}>Giám sát:</span>
                <span style={s.value}>
                  {acc.is_online
                    ? `🟢 ${acc.broadcasting ? 'Đang phát' : 'Online'} (${acc.viewer_count} xem)`
                    : '⚪ Offline'}
                </span>
              </div>
              {acc.note && (
                <div style={s.infoRow}>
                  <span style={s.label}>Ghi chú:</span>
                  <span style={{ ...s.value, fontStyle: 'italic', color: '#94a3b8' }}>{acc.note}</span>
                </div>
              )}
            </div>

            {/* Hàng 3: Các nút chức năng (Bấm vừa tay trên Mobile) */}
            <div style={s.cardActions}>
              <button style={s.actionBtnEdit} onClick={() => setEditing(acc)}>Sửa</button>
              <button style={s.actionBtnRenew} onClick={() => setRenewTarget(acc)}>Gia hạn</button>
              <button style={s.actionBtnLock} onClick={() => handleToggleLock(acc)}>
                {acc.status === 'locked' ? 'Mở khoá' : 'Khoá'}
              </button>
              <button style={s.actionBtnDelete} onClick={() => handleDelete(acc.id)}>Xoá</button>
            </div>
          </div>
        ))}

        {accounts.length === 0 && !loading && (
          <div style={s.emptyBox}>Không tìm thấy tài khoản nào</div>
        )}
      </div>

      {/* Modal Tạo mới / Chỉnh sửa tài khoản */}
      {editing && (
        <AccountModal
          account={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {/* Modal Gia hạn nhanh */}
      {renewTarget && (
        <div style={s.modalOverlay}>
          <div style={s.modalCard}>
            <h3 style={s.modalTitle}>Gia hạn: {renewTarget.username}</h3>
            <label style={s.fieldLabel}>Số ngày cộng thêm:</label>
            <input
              style={s.modalInput}
              type="number"
              value={renewDays}
              onChange={(e) => setRenewDays(e.target.value)}
              placeholder="Nhập số ngày"
            />
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              {[30, 90, 180, 365].map((d) => (
                <button key={d} style={s.presetBtn} onClick={() => setRenewDays(d)}>
                  +{d} ngày
                </button>
              ))}
            </div>
            <div style={s.modalActions}>
              <button style={s.confirmBtn} onClick={handleRenew}>Xác nhận gia hạn</button>
              <button style={s.cancelBtn} onClick={() => setRenewTarget(null)}>Hủy</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountModal({ account, onClose, onSave }) {
  const [form, setForm] = useState({
    id: account?.id,
    username: account?.username || '',
    password: '',
    full_name: account?.full_name || '',
    note: account?.note || '',
    expiry_date: account?.expiry_date ? account.expiry_date.slice(0, 16) : '',
  });

  function setPreset(months) {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    setForm({ ...form, expiry_date: d.toISOString().slice(0, 16) });
  }

  return (
    <div style={s.modalOverlay}>
      <div style={s.modalCard}>
        <h3 style={s.modalTitle}>{account ? 'Sửa Tài Khoản' : 'Tạo Tài Khoản Mới'}</h3>
        
        <label style={s.fieldLabel}>Tên đăng nhập:</label>
        <input
          style={s.modalInput}
          placeholder="Nhập Username"
          value={form.username}
          disabled={!!account}
          onChange={(e) => setForm({ ...form, username: e.target.value })}
        />

        <label style={s.fieldLabel}>{account ? 'Mật khẩu mới (để trống nếu giữ nguyên):' : 'Mật khẩu:'}</label>
        <input
          style={s.modalInput}
          placeholder="Mật khẩu"
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />

        <label style={s.fieldLabel}>Họ và tên khách hàng:</label>
        <input
          style={s.modalInput}
          placeholder="Nhập họ tên"
          value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
        />

        <label style={s.fieldLabel}>Ghi chú:</label>
        <input
          style={s.modalInput}
          placeholder="SĐT, Zalo hoặc thông tin phụ"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />

        <label style={s.fieldLabel}>Thời hạn hết hạn:</label>
        <input
          style={s.modalInput}
          type="datetime-local"
          value={form.expiry_date}
          onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
        />

        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {[1, 3, 6, 12].map((m) => (
            <button key={m} style={s.presetBtn} onClick={() => setPreset(m)}>{m} tháng</button>
          ))}
        </div>

        <div style={s.modalActions}>
          <button
            style={s.confirmBtn}
            onClick={() => {
              if (!form.username || (!account && !form.password) || !form.expiry_date) {
                alert('Vui lòng nhập đủ Username, Mật khẩu và Thời hạn');
                return;
              }
              onSave(form);
            }}
          >
            Lưu Tài Khoản
          </button>
          <button style={s.cancelBtn} onClick={onClose}>Huỷ</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100dvh',
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '12px 14px 40px 14px',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: '1px solid #334155',
  },
  h2: { fontSize: 18, margin: 0, fontWeight: 700, color: '#f8fafc' },
  subH2: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  logoutBtn: {
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 16,
  },
  searchBar: {
    width: '100%',
    padding: '11px 12px',
    borderRadius: 8,
    border: '1px solid #334155',
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    fontSize: 14,
    boxSizing: 'border-box',
  },
  selectFilter: {
    width: '100%',
    padding: '11px 12px',
    borderRadius: 8,
    border: '1px solid #334155',
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    fontSize: 14,
    boxSizing: 'border-box',
  },
  addBtn: {
    width: '100%',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: 8,
    padding: '12px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    textAlign: 'center',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
  },
  cardItem: {
    backgroundColor: '#1e293b',
    borderRadius: 12,
    padding: 14,
    border: '1px solid #334155',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: '1px solid #334155',
  },
  usernameText: {
    fontSize: 15,
    fontWeight: 700,
    color: '#f8fafc',
  },
  statusPill: {
    color: '#ffffff',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    fontSize: 13,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: { color: '#94a3b8' },
  value: { color: '#e2e8f0' },
  cardActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 6,
    marginTop: 4,
    paddingTop: 8,
    borderTop: '1px solid #263248',
  },
  actionBtnEdit: {
    backgroundColor: '#334155',
    color: '#f8fafc',
    border: 'none',
    borderRadius: 6,
    padding: '9px 0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnRenew: {
    backgroundColor: '#0284c7',
    color: '#ffffff',
    border: 'none',
    borderRadius: 6,
    padding: '9px 0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnLock: {
    backgroundColor: '#d97706',
    color: '#ffffff',
    border: 'none',
    borderRadius: 6,
    padding: '9px 0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtnDelete: {
    backgroundColor: '#dc2626',
    color: '#ffffff',
    border: 'none',
    borderRadius: 6,
    padding: '9px 0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  emptyBox: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '30px 10px',
    color: '#94a3b8',
    backgroundColor: '#1e293b',
    borderRadius: 12,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: 14,
  },
  modalCard: {
    backgroundColor: '#1e293b',
    borderRadius: 14,
    padding: 18,
    width: '100%',
    maxWidth: 400,
    maxHeight: '90dvh',
    overflowY: 'auto',
    border: '1px solid #334155',
    boxSizing: 'border-box',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 12,
    marginTop: 0,
    color: '#f8fafc',
  },
  fieldLabel: {
    fontSize: 12,
    color: '#94a3b8',
    marginBottom: 4,
    display: 'block',
  },
  modalInput: {
    width: '100%',
    padding: '11px 12px',
    borderRadius: 8,
    border: '1px solid #334155',
    backgroundColor: '#0f172a',
    color: '#f8fafc',
    fontSize: 14,
    marginBottom: 10,
    boxSizing: 'border-box',
  },
  presetBtn: {
    flex: 1,
    padding: '7px 0',
    backgroundColor: '#334155',
    color: '#f8fafc',
    border: 'none',
    borderRadius: 6,
    fontSize: 11,
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    gap: 8,
    marginTop: 8,
  },
  confirmBtn: {
    flex: 2,
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: 8,
    padding: '12px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  cancelBtn: {
    flex: 1,
    backgroundColor: '#475569',
    color: '#ffffff',
    border: 'none',
    borderRadius: 8,
    padding: '12px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};